import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Flex } from '@theme-ui/components';
import styled from '@emotion/styled';

import GiftFormTextField from '@app/components/input/GiftFormTextField';
import TextArea from '@app/components/input/TextArea';
import useValidationMessage from '@app/hooks/useValidationMessage';
import { GiftProductInputFormConstants } from '@app/pages/Pricing/components/GiftProductLayout/GiftProductInputFormConstants';
import { rem } from '@app/styles/theme/layout';

import messages from './GiftProduct.messages';

const ErrorMessage = styled.p`
  color: ${({ theme }) => theme.colors.red};
  font-size: ${rem(14)};
`;

const InputLabelMessage = styled.p`
  font-size: ${rem(14)};
  margin-bottom: ${rem(8)};
`;

type GiftProductInputFormProps = {
  namespace?: string;
  giftFormId: string;
  handleOnAddToCartClick: (data) => void;
};

export default function GiftProductInputForm({
  giftFormId,
  handleOnAddToCartClick,
}: GiftProductInputFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useFormContext();

  const { msg } = useValidationMessage(messages);

  const intl = useIntl();

  const onSubmit = (data) => {
    handleOnAddToCartClick(data);
  };

  return (
    /* "handleSubmit" will validate your inputs before invoking "onSubmit" */
    <form
      id={giftFormId}
      onSubmit={handleSubmit(onSubmit)}
      data-testid="gift-card-form"
    >
      <Flex
        sx={{
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ width: ['50%'] }} px={2} my={2}>
          <InputLabelMessage>
            <FormattedMessage {...messages.senderName} />
          </InputLabelMessage>
          <GiftFormTextField
            {...register(`${GiftProductInputFormConstants.SenderName}`, {
              required: {
                value: true,
                message: msg({
                  type: 'required',
                  key: 'senderName',
                }),
              },
            })}
            maxLength={128}
            data-testid="sender-name"
            isValid={!errors[GiftProductInputFormConstants.SenderName]}
            placeholder={messages.senderNamePlaceHolder.defaultMessage}
          />
          {errors[GiftProductInputFormConstants.SenderName] && (
            <ErrorMessage data-testid="sender-name-error">
              <FormattedMessage {...messages.pleaseAddSenderName} />
            </ErrorMessage>
          )}
        </Box>
        <Box sx={{ width: ['50%'] }} px={2} my={2}>
          <InputLabelMessage>
            <FormattedMessage {...messages.senderEmailAddress} />
          </InputLabelMessage>
          <GiftFormTextField
            {...register(
              `${GiftProductInputFormConstants.SenderEmailAddress}`,
              {
                required: {
                  value: true,
                  message: msg({
                    type: 'required',
                    key: 'senderEmailAddress',
                  }),
                },
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: messages.pleaseEnterValidEmailAddress.defaultMessage,
                },
              },
            )}
            maxLength={128}
            data-testid="sender-email"
            isValid={!errors[GiftProductInputFormConstants.SenderEmailAddress]}
            placeholder={messages.senderEmailAddressPlaceHolder.defaultMessage}
          />
          {errors[GiftProductInputFormConstants.SenderEmailAddress] &&
            (errors[GiftProductInputFormConstants.SenderEmailAddress]?.type ===
              'required' ||
              errors[GiftProductInputFormConstants.SenderEmailAddress]?.type ===
                'pattern') && (
              <ErrorMessage data-testid="sender-email-error">
                <FormattedMessage {...messages.pleaseEnterValidEmailAddress} />
              </ErrorMessage>
            )}
        </Box>
        <Box sx={{ width: ['50%'] }} px={2} my={2}>
          <InputLabelMessage>
            <FormattedMessage {...messages.recipientName} />
          </InputLabelMessage>
          <GiftFormTextField
            {...register(`${GiftProductInputFormConstants.RecipientName}`, {
              required: {
                value: true,
                message: msg({
                  type: 'required',
                  key: 'recipientName',
                }),
              },
            })}
            maxLength={128}
            data-testid="recipient-name"
            isValid={!errors[GiftProductInputFormConstants.RecipientName]}
            placeholder={messages.recipientNamePlaceHolder.defaultMessage}
          />
          {errors[GiftProductInputFormConstants.RecipientName] && (
            <ErrorMessage data-testid="recipient-name-error">
              <FormattedMessage {...messages.pleaseAddRecipientName} />
            </ErrorMessage>
          )}
        </Box>
        <Box sx={{ width: ['50%'] }} px={2} my={2}>
          <InputLabelMessage>
            <FormattedMessage {...messages.recipientEmailAddress} />
          </InputLabelMessage>
          <GiftFormTextField
            {...register(
              `${GiftProductInputFormConstants.RecipientEmailAddress}`,
              {
                required: {
                  value: true,
                  message: msg({
                    type: 'required',
                    key: 'recipientEmailAddress',
                  }),
                },
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: messages.pleaseEnterValidEmailAddress.defaultMessage,
                },
              },
            )}
            maxLength={128}
            data-testid="recipient-email"
            isValid={
              !errors[GiftProductInputFormConstants.RecipientEmailAddress]
            }
            placeholder={
              messages.recipientEmailAddressPlaceHolder.defaultMessage
            }
          />
          {errors[GiftProductInputFormConstants.RecipientEmailAddress] &&
            (errors[GiftProductInputFormConstants.RecipientEmailAddress]
              ?.type === 'required' ||
              errors[GiftProductInputFormConstants.RecipientEmailAddress]
                ?.type === 'pattern') && (
              <ErrorMessage data-testid="recipient-email-error">
                <FormattedMessage {...messages.pleaseEnterValidEmailAddress} />
              </ErrorMessage>
            )}
        </Box>
        <Box sx={{ width: ['100%'] }} px={2} my={2}>
          <InputLabelMessage>
            <FormattedMessage {...messages.giftMessage} />
          </InputLabelMessage>
          <TextArea
            {...register(`${GiftProductInputFormConstants.GiftMessage}`)}
            maxLength={256}
            data-testid="gift-message"
            isValid={!errors[GiftProductInputFormConstants.GiftMessage]}
            placeholder={intl.formatMessage(messages.giftMessage)}
          />
        </Box>
      </Flex>
    </form>
  );
}
